<template>
    <v-form :id="id" novalidate lazy-validation @submit.prevent="submit">
        <slot></slot>
    </v-form>
</template>

<script>
    import { validationMixin } from "vuelidate"
    import { maxLength, maxValue, minLength, minValue, required } from "vuelidate/lib/validators"
    import { buildError, maxDate, minDate, noPoBox, notInPast, requireOne } from "@/util/validation"
    import RemoteValidator from "@/service/RemoteValidator"

    const defaultMessages = {
        en: {
            rule: {
                invalid: "Invalid Field",
                required: "Required Field",
                requireTrue: "Must be checked",
                requireAny: "Must select at least one",
                minAge: "Must be at least {min} years old",
                minLength: "Must be at least {min} characters",
                maxLength: "Must be at most {max} characters",
                minSelections: "Must select at least {min} option",
                maxSelections: "No more than {max} options can be selected",
                minValue: "Must be at least {min}",
                maxValue: "Must be at most {max}",
                notInFuture: "Must not be in the future",
                notInPast: "Must not be in the past",
                noPoxBox: "P.O. Box Not Allowed",
                password: "Does Not Meet Password Requirements",
                sameAsPassword: "Passwords Do Not Match",
            },
        },
    }

    export default {
        mixins: [validationMixin],
        provide() {
            return {
                vcform: {
                    applyValidator: this.applyValidator,
                    remoteValidator: this.remoteValidator,
                    emitFieldEvent: this.emitFieldEvent,
                    registerInput: this.registerInput,
                    deregisterInput: this.deregisterInput,
                    errorMessage: this.errorMessage,
                    defaultErrorMessage: "",
                    updateInput: this.updateInput,
                    isValid: () => this.valid,
                },
            }
        },

        props: {
            id: {
                type: String,
            },
            inline: {
                type: Boolean,
                default: false,
            },
            messages: {
                type: Object,
            },
            validations: {
                type: Object,
            },
            preSubmit: {
                type: Function,
                default: () => {
                    return () => {
                    }
                },
            },
            validateImmediate: {
                type: Boolean,
                default: false,
            },
            remoteValidator: {
                type: RemoteValidator,
            },
        },

        data() {
            return {
                data: {},
                fieldMap: {},
                customErrors: {},
            }
        },

        computed: {
            valid() {
                if (this.$v.$invalid) {
                    return false
                }

                if (Object.keys(this.customErrors).length) {
                    return false
                }

                return true
            },

            invalid() {
                return !this.valid
            },
        },

        validations() {
            let validations = {}

            Object.values(this.fieldMap).forEach(field => {
                let fieldValidations = {}
                field.applyValidations(fieldValidations)

                if (this.validations && this.validations[field.id]) {
                    for (const key of Object.keys(this.validations[field.id])) {
                        this.applyValidator(fieldValidations, field, key, this.validations[field.id][key])
                    }
                }

                validations[field.id] = fieldValidations
            })

            return { data: validations }
        },

        methods: {
            emitFieldEvent(type, field, data) {
                return this.$emit(`field:${type}`, {
                    data,
                    field,
                })
            },

            async submit(event) {
                let promises = Object.values(this.fieldMap).map(field => {
                    return this.validateInput(field, true)
                })

                await Promise.all(promises)

                if (this.valid) {
                    await this.preSubmit(this, this.fieldMap)
                }

                if (this.valid) {
                    this.$emit("submit", event)
                    return Promise.resolve()
                }

                this.$nextTick(() => {
                    let invalidElement = document.querySelector(".is-invalid")

                    if (invalidElement) {
                        invalidElement.scrollIntoView({
                            block: "center",
                        })
                    }
                })

                this.$emit("submit:invalid")
                return Promise.resolve()
            },

            applyValidator(target, field, validatorId, validator) {
                let convertedValidator = convertValidator(validatorId, validator)

                if (convertedValidator) {
                    target[validatorId] = convertedValidator
                }
            },

            applyCustomErrors(fieldId, valid, errors) {
                let field = this.fieldMap[fieldId]

                if (!field) {
                    return
                }

                this.$set(this.customErrors, fieldId, errors)
                field.setValidity(valid, errors)
            },

            errorMessage(field, error) {
                let messageKey = `rule.${error.id}`

                if (error.$remote && this.translationExists(error.message)) {
                    messageKey = error.message
                }

                if (this.translationExists(messageKey, error.params)) {
                    return this.translate(messageKey, error.params)
                }

                return this.translate("rule.invalid", error.params)
            },

            registerInput(input) {
                this.fieldMap[input.id] = input
                this.$set(this.data, input.id, input.value)

                input.$watch("value", value => {
                    this.data[input.id] = value

                    if (this.customErrors[input.id]) {
                        this.$delete(this.customErrors, input.id)
                    }

                })

                input.$on("validate", () => {
                    this.validateInput(input, true)
                })

                input.setValidity(true)

                if (this.validateImmediate) {
                    this.$nextTick(() => {
                        this.validateInput(input, false)
                    })
                }
            },

            translationExists(key) {
                if (this.messages && this.messages.en && !!getMessageValue(key, this.messages.en, null, false)) {
                    return true
                }

                if (this.$te && this.$te(key)) {
                    return true
                }

                return !!getMessageValue(key, defaultMessages.en, null, false)
            },

            translate(key, params) {
                let value = ""

                if (this.messages && this.messages.en) {
                    value = getMessageValue(key, this.messages.en, params)
                }

                if (!value && this.$t) {
                    value = this.$t(key, params)
                }

                if (!value) {
                    value = getMessageValue(key, defaultMessages.en, params)
                }

                return value || ""
            },

            validateInput(input, touch) {
                return new Promise(resolve => {
                    let fieldModel = this.$v.data[input.id]

                    if (!fieldModel) {
                        return resolve()
                    }

                    if (touch) {
                        fieldModel.$touch()
                    }


                    const finisher = () => {
                        input.setValidity(!fieldModel.$invalid, parseErrors(input, fieldModel))
                        resolve()
                    }

                    if (fieldModel.$pending) {
                        const unwatch = this.$watch(
                            () => {
                                return fieldModel.$pending
                            },
                            () => {
                                finisher()
                                unwatch()
                            },
                        )
                    } else {
                        finisher()
                    }
                })
            },

            deregisterInput(input) {
                delete this.fieldMap[input.id]
            },

            updateInput(input) {
            },

            setValidity(valid, errors) {
                Object.values(this.fieldMap).forEach(field => field.setValidity(valid, errors))
            },

            touch() {
                Object.values(this.fieldMap).forEach(field => field.touch())
            },

            reset() {
                Object.values(this.fieldMap).forEach(field => field.reset())
            },
        },
    }

    function convertValidator(validatorId, validator) {
        if (typeof validator === "function") {
            return validator
        }

        switch (validatorId) {
            case "required": {
                return validator ? required : null
            }
            case "requireTrue": {
                return validator ? value => value === true : null
            }
            case "requireAny": {
                return validator ? value => Array.isArray(value) && value.length !== 0 : null
            }
            case "minLength": {
                return minLength(validator)
            }
            case "maxLength": {
                return maxLength(validator)
            }
            case "minSelections": {
                return minLength(validator)
            }
            case "maxSelections": {
                return maxLength(validator)
            }
            case "minValue": {
                return minValue(validator)
            }
            case "maxValue": {
                return maxValue(validator)
            }
            case "minDate": {
                return minDate(validator)
            }
            case "maxDate": {
                return maxDate(validator)
            }
            case "notInPast": {
                return validator ? notInPast() : null
            }
            case "noPoBox": {
                return validator ? noPoBox : null
            }
            case "requireOne": {
                return requireOne(validator)
            }
            default: {
                return null
            }
        }
    }

    // translate errors from vuelidate into our own error structure
    function parseErrors(input, fieldModel) {
        let errors = {}

        for (const key of Object.keys(fieldModel)) {
            if (key[0] === "$") continue
            if (fieldModel[key] !== false) continue
            errors[key] = buildError(key, fieldModel.$model, input.label, fieldModel.$params[key])
        }

        return errors
    }

    function interpolateValue(value, params) {
        return value.replace(/{\s*([a-zA-Z0-9_]+)\s*}/, function(match, name) {
            return params[name] || ""
        })
    }

    function getMessageValue(key, obj, params = null, interpolate = true) {
        if (!obj || !key) {
            return null
        }

        const path = key.split(".")

        for (let i = 0; i < path.length - 1; i++) {
            obj = obj[path[i]]

            if (!obj) {
                return null
            }
        }

        let value = obj[path[path.length - 1]]

        if (interpolate && value && value.indexOf("{") >= 0) {
            value = interpolateValue(value, params)
        }

        return value
    }
</script>
